/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import config from 'js/app/config';
import Retracked from 'js/app/retracked';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import type { TrackingContextData } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { LoadingSection } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import type {
  EntWebsiteBlockMarketoModuleQuery,
  EntWebsiteMarketoModuleFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import { EntWebsiteDomainContext } from 'bundles/ent-website/components/EntWebsiteDomainContext';
import {
  LEAN_DATA_CALENDAR_LOCAL_STORAGE_KEY,
  MARKETO_MUNCHKIN_ID,
  MARKETO_URL,
} from 'bundles/ent-website/constants/marketo';
import type { DOMAINS } from 'bundles/ent-website/constants/navigation';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { beforeSubmit, beforeSuccess, onLoad } from 'bundles/ent-website/contentful/modules/MarketoFormModules';
import { AssetDownloadLink } from 'bundles/ent-website/contentful/modules/MarketoFormModules/AssetDownload';
import type { MktoForms2 } from 'bundles/ent-website/globals';
import { entWebsiteBlockMarketoModuleQuery } from 'bundles/ent-website/queries/EntWebsiteMarketoModule';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import EnterpriseWebsiteExperiments from 'bundles/epic/clients/EnterpriseWebsite';
import { isSSR } from 'bundles/ondemand/utils/isSSR';

import 'css!./__styles__/marketoInputs';

type MarketoWrapperProps = BlockModuleInnerProps<EntWebsiteMarketoModuleFragment>;

type ContentfulFormContextContainerProps = Pick<MarketoWrapperProps, 'content'> & {
  mktoForms2: MktoForms2 | undefined;
  mktoFormId: string;
  optionalAssetUrl?: string;
  useFloatingLabels?: boolean;
};

type ContentfulFormProps = ContentfulFormContextContainerProps & {
  eventData: TrackingContextData;
};

type TrackingEventingV3 = (
  formId: string,
  formName: string,
  action: 'form_blur' | 'form_focus' | 'form_submit' | 'form_success' | 'form_validate',
  redirectURL?: string,
  validated?: boolean
) => void;

export type ModificationProps = {
  mktoFormId: string;
  router: InjectedRouter;
  domain: keyof typeof DOMAINS;
};

export const loadFormModifications = ({ mktoFormId, router, domain }: ModificationProps) => {
  onLoad.forEach((cb) => cb({ mktoFormId, router, domain }));
};

export const onSubmitModifications = ({ mktoFormId, router, domain }: ModificationProps) => {
  beforeSubmit.forEach((cb) => cb({ mktoFormId, router, domain }));
};

export const onSuccessModifications = ({ mktoFormId, router, domain }: ModificationProps) => {
  beforeSuccess.forEach((cb) => cb({ mktoFormId, router, domain }));
};

export const generateMktoFormId = (mktoFormId: string) => {
  return `mktoForm_${mktoFormId}`;
};

type FormTrackingState = {
  // Undefined is to ensure eventing starts when the user interacts, not render
  formBlurred: boolean | undefined;
  formFocused: boolean | undefined;
};

export const COMPANY_SIZE_MKTO_FIELD_NAME = 'Employee_Range__c';

export const isBookItTest = (pathname: string) => {
  return EnterpriseWebsiteExperiments.get('bookitPathnames')?.includes(pathname);
};

export const getMarketoRedirectURLFromPathname = (
  pathname: string,
  redirectUrlFromContentful?: string
): string | undefined => {
  let redirectURL;

  // Excluding for BookIt test ENTING-1304
  if (isBookItTest(pathname)) return undefined;

  // 1st priority
  if (redirectUrlFromContentful) {
    return redirectUrlFromContentful;
  }

  // 2nd priority
  const isBusinessPage = pathname.includes('ent-website') || pathname.includes('business');
  const isWebinar = pathname.includes('webinar') || pathname.includes('webcast');
  const isEvent = pathname.includes('event');

  const isBusinessEvent = isBusinessPage && isEvent;
  const isBusinessWebinar = isBusinessPage && isWebinar;
  const isCampusEvent = pathname.includes('/campus/') && isEvent;
  const isCampusWebinar = pathname.includes('/campus/') && isWebinar;
  const isGovWebinar = pathname.includes('/government/') && isWebinar;
  const isGovEvent = pathname.includes('/government/') && isEvent;

  // Pages provided by Luis Esquivel
  if (isGovWebinar) {
    redirectURL = `${config.url.base}government/resources/webinar/thank-you`;
  } else if (isGovEvent) {
    redirectURL = `${config.url.base}government/resources/event/thank-you`;
  } else if (isBusinessEvent) {
    redirectURL = `${config.url.base}business/resources/event/thank-you-for-registering`;
  } else if (isBusinessWebinar) {
    redirectURL = `${config.url.base}business/resources/webinar/thank-you-for-registering`;
  } else if (isCampusWebinar) {
    redirectURL = `${config.url.base}campus/resources/webinar/thank-you-for-registration`;
  } else if (isCampusEvent) {
    redirectURL = `${config.url.base}campus/resources/event/thank-you-for-registration`;
  }

  return redirectURL;
};

export const isContactSchedulerTest = (pathname: string, fields?: Record<string, string>) => {
  return (
    fields?.[COMPANY_SIZE_MKTO_FIELD_NAME] === '501-1000' &&
    pathname === '/business/learn-more' &&
    EnterpriseWebsiteExperiments.get('enableMidMarketThankYou')
  );
};

const styles = {
  formContainerWithFloatLabels: css`
    .mktoFormCol {
      min-height: 0 !important;
    }

    .mktoHtmlText {
      margin-top: 24px !important;
      padding: 0 24px;
    }

    .mktoButtonRow {
      margin-bottom: 12px;
      margin-top: 24px !important;
    }

    .mktoFormRow {
      .selectParent {
        select {
          cursor: pointer;
        }
      }

      @media only screen and (max-width: 1350px) {
        flex-direction: column !important;
      }

      label.not-empty,
      .mktoFieldWrap:focus-within label,
      .mktoFieldDescriptor:focus-within label {
        top: -12px !important;
        font-size: 14px;
        padding: 0 4px;
        left: 24px;
      }

      label {
        padding: 0 4px;
        font-size: 18px;
        font-weight: 400 !important;
        position: absolute;
        top: 12px;
        left: 26px;
        pointer-events: none;
        margin: 0;
        display: inline-flex;
        width: fit-content !important;
        max-width: 95% !important;
        background: #fff;
        transition: all 0.15s;

        @media screen and (max-width: 640px) {
          font-size: 16px;
        }
      }
    }

    .mktoAsterix {
      display: none !important;
    }

    .scriptParent {
      margin: 0 !important;
    }

    .radioListParent,
    .checkboxListParent {
      .mktoLabel {
        margin-bottom: 12px;
        font-weight: bold !important;
      }

      label {
        position: unset !important;
        top: 0 !important;
        left: 0 !important;
        font-size: 18px !important;
        padding: 0 !important;
        background: transparent !important;
      }

      .mktoRadioList {
        display: flex;
        align-items: center;
        margin-top: 12px;

        input,
        label {
          display: inline-flex;
          position: unset;
          margin: 0;
        }

        label {
          margin-left: 12px;
          margin-right: 18px;
        }
      }
    }
  `,
};

export const ContentfulForm: React.FC<ContentfulFormProps> = ({
  mktoFormId,
  mktoForms2,
  eventData,
  content,
  optionalAssetUrl,
  useFloatingLabels,
}) => {
  const redirectUrlFromContentful = useMemo(
    () => content?.forms?.redirectUrl ?? undefined,
    [content?.forms?.redirectUrl]
  );
  const [formLoaded, setFormLoaded] = useState(false);
  const [formTrackingState, setFormTrackingState] = useState<FormTrackingState>({
    formBlurred: undefined,
    formFocused: undefined,
  });
  // This is required because both focus and blur occur every input change.
  // If we do not defer the state update until next tick, we get both
  // focus and blur every change.
  const [deferredFunc, setDeferredFunc] = useState<number>();
  const router = useRouter();
  const { domain } = useContext(EntWebsiteDomainContext);

  const formTrackingName = eventingTransform(`${content.__typename}_Form_${content?.slug}`);
  const { formBlurred, formFocused } = formTrackingState;
  const formEl = useRef(null);
  const tracker = useTracker();

  const trackEventingV3: TrackingEventingV3 = React.useCallback<TrackingEventingV3>(
    (formId, formName, action, redirectURL, validated) => {
      tracker('marketo_form_action', {
        marketoForm: {
          formId,
          formName,
          formAction: action,
          ...(redirectURL && { redirectionUrlOnSubmission: redirectURL }),
          ...(validated !== undefined && { formValidated: validated }),
        },
      });
    },
    [tracker]
  );
  useEffect(() => {
    if (mktoForms2 && !formLoaded) {
      mktoForms2.whenRendered(() => {
        if (useFloatingLabels) {
          const inputElements = document.querySelectorAll(
            `#mktoForm_${mktoFormId} input, #mktoForm_${mktoFormId} select, #mktoForm_${mktoFormId} textarea`
          );

          inputElements?.forEach((inputEl) => {
            if (inputEl?.tagName === 'SELECT') {
              inputEl?.parentElement?.classList.add('selectParent');
            }
            inputEl.addEventListener('blur', (event: Event) => {
              if ((event?.target as HTMLInputElement)?.value) {
                (event?.currentTarget as HTMLInputElement)?.parentElement
                  ?.querySelector('label')
                  ?.classList.add('not-empty');
              } else {
                (event?.currentTarget as HTMLInputElement)?.parentElement
                  ?.querySelector('label')
                  ?.classList.remove('not-empty');
              }
            });
          });

          const radioLists = document.querySelectorAll('.mktoRadioList');
          const checkboxList = document.querySelectorAll('.mktoCheckboxList');
          const scriptEls = document.querySelectorAll('.mktoHtmlText > script');

          const labels: NodeListOf<HTMLElement> = document.querySelectorAll(`#mktoForm_${mktoFormId} label`);

          labels.forEach((label) => {
            if (label?.innerText?.[label?.innerText?.length - 1] === ':') {
              // eslint-disable-next-line no-param-reassign
              label.innerText = label?.innerText?.slice(0, label?.innerText?.length - 1);
            }
          });

          radioLists.forEach((radioEl) => {
            radioEl?.parentElement?.classList.add('radioListParent');
          });
          checkboxList.forEach((radioEl) => {
            radioEl?.parentElement?.classList.add('checkboxListParent');
          });
          scriptEls?.forEach((scriptEl) => {
            scriptEl?.parentElement?.classList.add('scriptParent');
          });
        }
      });

      mktoForms2.loadForm(MARKETO_URL, MARKETO_MUNCHKIN_ID, mktoFormId, () => {
        loadFormModifications({ mktoFormId, router, domain });
        setFormLoaded(true);

        const mktoForm = mktoForms2.getForm(mktoFormId);

        if (mktoForm) {
          mktoForm.onValidate((validated) => {
            // If false, we have a failed validation

            trackEventingV3(mktoFormId, formTrackingName, 'form_validate', undefined, validated);
            Retracked.trackComponent(
              eventData,
              { mktoFormId, validated }, // Data
              formTrackingName,
              'formOnValidate'
            );
          });
          mktoForm.onSubmit(() => {
            onSubmitModifications({ mktoFormId, router, domain });
            // Submission is happening,
            // Does not promise Marketo will recieve
            trackEventingV3(mktoFormId, formTrackingName, 'form_submit');
            Retracked.trackComponent(
              eventData,
              { mktoFormId }, // Data
              formTrackingName,
              'formOnSubmit'
            );
          });
          mktoForm.onSuccess((_, redirectUrl) => {
            onSuccessModifications({ mktoFormId, router, domain });
            // Submission successfully made it to Marketo
            // There is no failure handler exposed to the client
            trackEventingV3(mktoFormId, formTrackingName, 'form_success', redirectUrl);
            Retracked.trackComponent(
              eventData,
              { mktoFormId, redirectUrl }, // Data
              formTrackingName,
              'formOnSuccess'
            );

            // If qualifies for experiment: https://tools.coursera.org/epic/experiment/PsF_kkAWEe2ZehIMNnt55w
            if (isContactSchedulerTest(window.location.pathname, _)) {
              window.location.href = `${config.url.base}business/calendar-thank-you`;
              return false;
            }
            if (isBookItTest(window.location.pathname))
              localStorageEx.setItem<boolean>(LEAN_DATA_CALENDAR_LOCAL_STORAGE_KEY, true, String);

            const redirectURL = getMarketoRedirectURLFromPathname(window.location.pathname, redirectUrlFromContentful);

            if (redirectURL && EnterpriseWebsiteExperiments.get('marketoCustomRedirectURL')) {
              // Redirect to the specified URL
              window.location.href = redirectURL;

              // Prevent Marketo's default onSuccess event
              return false;
              // 3rd priority is default Marketo behaviour
            } else {
              // Follow redirect / download / refresh logic.
              // Setting to false can be useful for local development.
              return true;
            }
          });
        }
      });
    }
  }, [
    mktoFormId,
    mktoForms2,
    eventData,
    formTrackingName,
    router,
    redirectUrlFromContentful,
    formLoaded,
    domain,
    useFloatingLabels,
    trackEventingV3,
  ]);

  useEffect(() => {
    // This handles blur
    if (formBlurred === true) {
      trackEventingV3(mktoFormId, formTrackingName, 'form_blur');

      Retracked.trackComponent(
        eventData,
        { mktoFormId }, // Data
        formTrackingName,
        'formBlur'
      );
    }

    // This handles focus
    if (formFocused === true) {
      trackEventingV3(mktoFormId, formTrackingName, 'form_focus');
      Retracked.trackComponent(
        eventData,
        { mktoFormId }, // Data
        formTrackingName,
        'formFocus'
      );
    }
  }, [formBlurred, formFocused, mktoFormId, eventData, formTrackingName, trackEventingV3]);

  const handleFocus: React.FocusEventHandler<HTMLFormElement> = () => {
    if (deferredFunc) {
      clearTimeout(deferredFunc);
    }

    setDeferredFunc(
      window.setTimeout(() => {
        setFormTrackingState({
          formBlurred: false,
          formFocused: true,
        });
      })
    );
  };

  const handleBlur: React.FocusEventHandler<HTMLFormElement> = () => {
    if (deferredFunc) {
      clearTimeout(deferredFunc);
    }

    setDeferredFunc(
      window.setTimeout(() => {
        setFormTrackingState({
          formBlurred: true,
          formFocused: false,
        });
      })
    );
  };

  return (
    <div
      css={useFloatingLabels ? styles.formContainerWithFloatLabels : undefined}
      className={classNames({ useFloatingLabels })}
    >
      <Helmet>
        <script id={`mktoScript_${mktoFormId}`} src={`${MARKETO_URL}/js/forms2/js/forms2.min.js`} />
        {/* TODO: Placeholder, should be removed when we style our forms. */}
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@algolia/autocomplete-theme-classic" />
        {isBookItTest(router?.location?.pathname) && (
          <script>
            {`
              const orgId = '00D36000001HB4zEAG';
              const nodeName = 'C4C/C4B/C4G';
              let LDCalendaringObjName = 'LDCalendaring';
          
              (function (windowRef, documentRef, fileLocation, functionName) {
                windowRef.LeanDataCalendaringObjName = functionName;
                windowRef[functionName] = (...args) => {
                  windowRef[functionName].variables = args;
                };
                const scriptElement = documentRef.createElement('script');
                const targetScript = document.getElementsByTagName('script')[0];
                const targetHead = document.getElementsByTagName('head')[0];
                scriptElement.async = 1;
                scriptElement.src = fileLocation;
                if (targetScript) {
                  targetScript.parentNode.insertBefore(scriptElement, targetScript);
                } else if (targetHead) {
                  targetHead.append(scriptElement);
                }
              })(window, document, 'https://app.leandata.com/js-snippet/ld_calendaring.js', 'LDCalendaring');
              LDCalendaring(orgId, nodeName, true, {
                customInterface: {
                  afterSubmit: () => {
                    return false;
                  },
                },
              });
              (function () {
                LDCalendaringObjName = 'LDCalendaring';
                const submitHandlerRegister = (fName) => {
                  let registered = false;
                  console.log('registering mkto form...');
                  if (window[LDCalendaringObjName] && window[LDCalendaringObjName][fName]) {
                    registered = window[LDCalendaringObjName][fName]();
                  }
                  if (!registered) {
                    setTimeout(() => {
                      submitHandlerRegister(fName);
                    }, 1000);
                  }
                };
                submitHandlerRegister('MktoFormReadyCallback');
              })();
            `}
          </script>
        )}
      </Helmet>

      {!formLoaded && <LoadingSection />}

      <form
        onFocus={handleFocus}
        onBlur={handleBlur}
        // onSubmit never hits because of Marketos implementation
        // onInvalid never hits because of Marketos implementaiton
        data-testid={`mktoTestFormId-${mktoFormId}`}
        id={generateMktoFormId(mktoFormId)}
        ref={formEl}
      >
        <AssetDownloadLink assetUrl={optionalAssetUrl} />
      </form>
    </div>
  );
};

/**
 * This container is to pass the legacy context event data for Retracked
 */
const ContentfulFormContextContainer = (
  props: ContentfulFormContextContainerProps,
  context: { _eventData: TrackingContextData }
) => <ContentfulForm eventData={context._eventData} {...props} />;

ContentfulFormContextContainer.contextTypes = { _eventData: PropTypes.object };

/*
The helmet injection does work well when running locally via snowpack.
The <head/> injection of a script is non blocking so theres no way to know when
the window object has been modified. I added an interval that only triggers when
rendered by CSR.
*/
export const MarketoModule: React.FC<MarketoWrapperProps> = ({ content, assembly }) => {
  const [loaded, setLoaded] = useState(isSSR());
  const mktoForms2 = isSSR() ? undefined : window.MktoForms2;

  // WIP - we will use Contentful for localization. Fields to be renamed to formId and optionalAssetLong
  const mktoFormId = content?.forms?.formIdEnglish ?? '';
  const useFloatingLabels = EnterpriseWebsiteExperiments?.get('shortFormRollout') ?? true;
  const optionalAssetUrl = content?.assets?.optionalAssetEnglishLong ?? undefined;

  useEffect(() => {
    let interval: number;
    if (loaded === false) {
      interval = window.setInterval(() => {
        if (window.MktoForms2) {
          setLoaded(true);
        }
      }, 1000);
    }

    return () => {
      window.clearInterval(interval);
    };
  }, [loaded]);

  if (!mktoFormId) {
    return null;
  }

  function contentPanel() {
    return (
      <ContentfulFormContextContainer
        data-testid={`mktoTestId-${mktoFormId}`}
        mktoForms2={mktoForms2}
        mktoFormId={mktoFormId}
        content={content}
        optionalAssetUrl={optionalAssetUrl}
        useFloatingLabels={useFloatingLabels}
      />
    );
  }

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
};

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockMarketoModule = withBlockQuery<EntWebsiteMarketoModuleFragment, EntWebsiteBlockMarketoModuleQuery>(
  entWebsiteBlockMarketoModuleQuery
)(MarketoModule);
